import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { getDisplayName, getRolesSummary, getInitials } from "../utils/userDisplay";

export default function UserAccountMenu() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  if (!user) return null;

  const name = getDisplayName(user);
  const roles = getRolesSummary(user);
  const initials = getInitials(user);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0, flexShrink: 0 }}>
      {isSmUp && (
        <Box
          sx={{
            textAlign: "right",
            maxWidth: 140,
            display: { xs: "none", md: "block" },
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              color: "inherit",
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.9,
              display: "block",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {roles}
          </Typography>
        </Box>
      )}
      <IconButton
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="account menu"
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          border: "1px solid rgba(255,255,255,0.5)",
          p: 0.25,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.85rem",
            fontWeight: 800,
          }}
        >
          {initials}
        </Box>
      </IconButton>
      <Menu
        id="account-menu"
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { minWidth: 220, mt: 1, borderRadius: 2 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {roles}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          component={Link}
          to="/profile"
          onClick={() => setAnchor(null)}
        >
          <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.8 }} />
          My profile
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout();
            navigate("/", { replace: true });
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1, opacity: 0.8 }} />
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
}
